import React, { useState } from 'react';

const REEL_HIGHLIGHT_MAX_LENGTH = 500;

export const DEFAULT_REEL_HIGHLIGHT_PROMPT =
  'Highlight the standout feature, the problem it solves, and what makes the experience feel special.';

interface ReelHighlightFieldProps {
  disabled?: boolean;
  mode: 'create' | 'regenerate';
  onChange: (value: string) => void;
  value: string;
}

const ReelHighlightField: React.FC<ReelHighlightFieldProps> = ({ disabled = false, mode, onChange, value }) => (
  <label className="promo-highlight-field">
    <span className="promo-highlight-heading">
      <strong>What should stand out?</strong>
      <span>{mode === 'create' ? 'Shape the narration and emphasis.' : 'Regenerate to apply a new angle.'}</span>
    </span>
    <textarea
      aria-label="What should the reel highlight?"
      className="promo-highlight-input"
      disabled={disabled}
      maxLength={REEL_HIGHLIGHT_MAX_LENGTH}
      onChange={(event) => onChange(event.target.value)}
      rows={3}
      value={value}
    />
  </label>
);

interface CreateReelBlockProps {
  disabled: boolean;
  error: string | null;
  highlightPrompt: string;
  onCreate: (highlightPrompt: string) => void;
  onHighlightPromptChange: (highlightPrompt: string) => void;
}

export const CreateReelBlock: React.FC<CreateReelBlockProps> = ({
  disabled,
  error,
  highlightPrompt,
  onCreate,
  onHighlightPromptChange,
}) => (
  <div className="promo-share-block">
    <div className="promo-create-copy">
      <span className="promo-create-eyebrow">Today&apos;s reel</span>
      <strong>Turn this project into a shareable reel</strong>
      <span>Earn 1 credit per 10 verified views, up to 100 per reel.</span>
    </div>
    <ReelHighlightField
      disabled={disabled}
      mode="create"
      onChange={onHighlightPromptChange}
      value={highlightPrompt}
    />
    <button
      className="promo-create-btn"
      disabled={disabled}
      onClick={() => onCreate(highlightPrompt)}
      type="button"
    >
      Create reel
    </button>
    {error && <div className="promo-error">{error}</div>}
  </div>
);

interface RegenerateHighlightControlProps {
  disabled: boolean;
  highlightPrompt: string;
  onHighlightPromptChange: (highlightPrompt: string) => void;
  onRegenerate: (highlightPrompt: string) => Promise<boolean>;
}

export const RegenerateHighlightControl: React.FC<RegenerateHighlightControlProps> = ({
  disabled,
  highlightPrompt,
  onHighlightPromptChange,
  onRegenerate,
}) => {
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setRegenerating(true);
    const regenerated = await onRegenerate(highlightPrompt);

    if (!regenerated) setRegenerating(false);
  };

  const inputDisabled = disabled || regenerating;

  return (
    <div className="promo-regenerate-control">
      <ReelHighlightField
        disabled={inputDisabled}
        mode="regenerate"
        onChange={onHighlightPromptChange}
        value={highlightPrompt}
      />
      <button
        className="promo-regenerate-btn"
        disabled={inputDisabled}
        onClick={handleRegenerate}
        type="button"
      >
        {regenerating ? 'Regenerating…' : 'Regenerate with this focus'}
      </button>
    </div>
  );
};
